import { Injectable } from "@angular/core";
import { openBlobInNewTab, downloadBlob } from "../../../utils/blob-viewer.util";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import {
  catchError,
  map,
  switchMap,
  concatMap,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import { EMPTY, of, forkJoin } from "rxjs";
import { Store } from "@ngrx/store";

import { IssueTrackerService } from "@services/issue-tracker.service";
import { IssueTrackerActions } from "@store/actions/issue-tracker/issue-tracker.actions";
import { GetTicketsRequest } from "@models/ticket-data.model";
import { selectActiveUser } from "@store/selectors/user/user.selectors";

@Injectable()
export class IssueTrackerEffects {
  getTickets$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(IssueTrackerActions.getTickets),
      switchMap((action) => {
        return this.issueTrackerService.getTickets(action.request).pipe(
          map((response) =>
            IssueTrackerActions.getTicketsSuccess({
              tickets: response.tickets || response,
            })
          ),
          catchError((error) =>
            of(
              IssueTrackerActions.getTicketsFailure({
                error: error.message || "Failed to load tickets",
              })
            )
          )
        );
      })
    );
  });

  createTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssueTrackerActions.createTicket),
      switchMap(({ ticket, attachmentData, isPhi }) =>
        this.issueTrackerService.createTicket(ticket).pipe(
          map((createdTicket) =>
            IssueTrackerActions.createTicketSuccess({
              ticket: createdTicket,
              attachmentData,
              isPhi,
            })
          ),
          catchError((error) =>
            of(IssueTrackerActions.createTicketFailure({ error }))
          )
        )
      )
    )
  );

  createTicketSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssueTrackerActions.createTicketSuccess),
      switchMap(({ ticket, attachmentData, isPhi }) => {
        if (!attachmentData || attachmentData.length === 0) {
          return EMPTY;
        }
        const uploadRequests = attachmentData.map((data) =>
          this.issueTrackerService.uploadTicketAttachment(
            ticket.id,
            data.file,
            isPhi,
            data.dataEnrichmentDocumentType,
            data.dataEnrichmentDocumentDate
          )
        );

        return forkJoin(uploadRequests).pipe(
          concatMap((uploadResults) =>
            uploadResults.map((attachment) =>
              IssueTrackerActions.uploadTicketAttachmentSuccess({ attachment })
            )
          ),
          catchError((error) =>
            of(IssueTrackerActions.uploadTicketAttachmentFailure({ error }))
          )
        );
      })
    )
  );

  updateTicket$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(IssueTrackerActions.updateTicket),
      switchMap((action) => {
        return this.issueTrackerService
          .updateTicket(action.ticketId, action.updates)
          .pipe(
            map((ticket) =>
              IssueTrackerActions.updateTicketSuccess({ ticket })
            ),
            catchError((error) =>
              of(
                IssueTrackerActions.updateTicketFailure({
                  error: error.message || "Failed to update ticket",
                })
              )
            )
          );
      })
    );
  });

  addTicketComment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(IssueTrackerActions.addTicketComment),
      switchMap((action) => {
        return this.issueTrackerService
          .addTicketComment(action.ticketId, action.comment)
          .pipe(
            map((comment) => {
              return IssueTrackerActions.addTicketCommentSuccess({ comment });
            }),
            catchError((error) => {
              console.error("Comment API error:", error);
              return of(
                IssueTrackerActions.addTicketCommentFailure({
                  error: error.message || "Failed to add comment",
                })
              );
            })
          );
      })
    );
  });

  getTicketCategories$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(IssueTrackerActions.getTicketCategories),
      switchMap((action) => {
        return this.issueTrackerService.getTicketCategories().pipe(
          map((categories) =>
            IssueTrackerActions.getTicketCategoriesSuccess({ categories })
          ),
          catchError((error) =>
            of(
              IssueTrackerActions.getTicketCategoriesFailure({
                error: error.message || "Failed to load ticket categories",
              })
            )
          )
        );
      })
    );
  });

  populateCommentData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssueTrackerActions.addTicketCommentSuccess),
      withLatestFrom(this.store.select(selectActiveUser)),
      map(([action, user]) => {
        const populatedComment = {
          ...action.comment,
          createdByName:
            user?.loggedInUser?.firstName && user?.loggedInUser.lastName
              ? `${user.loggedInUser?.firstName} ${user.loggedInUser?.lastName}`
              : user?.email || "Unknown",
        };

        return IssueTrackerActions.populateCommentData({
          comment: populatedComment,
        });
      })
    )
  );

  populateAttachmentData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssueTrackerActions.uploadTicketAttachmentSuccess),
      withLatestFrom(this.store.select(selectActiveUser)),
      map(([action, user]) => {
        const populatedAttachment = {
          ...action.attachment,
          createdByName:
            user?.loggedInUser?.firstName && user?.loggedInUser?.lastName
              ? `${user.loggedInUser?.firstName} ${user.loggedInUser?.lastName}`
              : user?.loggedInUser?.emailAddress || "Unknown",
        };

        return IssueTrackerActions.populateAttachmentData({
          attachment: populatedAttachment,
        });
      })
    )
  );

  getTicketStatuses$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(IssueTrackerActions.getTicketStatuses),
      switchMap((action) => {
        return this.issueTrackerService.getTicketStatuses().pipe(
          map((statuses) =>
            IssueTrackerActions.getTicketStatusesSuccess({ statuses })
          ),
          catchError((error) =>
            of(
              IssueTrackerActions.getTicketStatusesFailure({
                error: error.message || "Failed to load ticket statuses",
              })
            )
          )
        );
      })
    );
  });

  uploadTicketAttachment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(IssueTrackerActions.uploadTicketAttachment),
      switchMap((action) => {
        return this.issueTrackerService
          .uploadTicketAttachment(action.ticketId, action.file, action.isPhi, action.dataEnrichmentDocumentType, action.dataEnrichmentDocumentDate)
          .pipe(
            map((attachment) => {
              return IssueTrackerActions.uploadTicketAttachmentSuccess({
                attachment: {
                  ...attachment,
                  ticketId: action.ticketId
                },
              });
            }),
            catchError((error) => {
              console.error("Attachment API error:", error);
              return of(
                IssueTrackerActions.uploadTicketAttachmentFailure({
                  error: error.message || "Failed to upload attachment",
                })
              );
            })
          );
      })
    );
  });

  updateTicketAttachment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(IssueTrackerActions.updateTicketAttachment),
      switchMap((action) => {
        return this.issueTrackerService
          .updateTicketAttachment(action.attachmentId, action.dataEnrichmentDocumentType, action.dataEnrichmentDocumentDate)
          .pipe(
            map((attachment) =>
              IssueTrackerActions.updateTicketAttachmentSuccess({ attachment })
            ),
            catchError((error) =>
              of(
                IssueTrackerActions.updateTicketAttachmentFailure({
                  error: error.message || "Failed to update attachment",
                })
              )
            )
          );
      })
    );
  });

  deleteTicketAttachment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(IssueTrackerActions.deleteTicketAttachment),
      switchMap((action) => {
        return this.issueTrackerService
          .deleteTicketAttachment(action.attachmentId)
          .pipe(
            map(() =>
              IssueTrackerActions.deleteTicketAttachmentSuccess({
                attachmentId: action.attachmentId,
              })
            ),
            catchError((error) =>
              of(
                IssueTrackerActions.deleteTicketAttachmentFailure({
                  error: error.message || "Failed to delete attachment",
                })
              )
            )
          );
      })
    );
  });

  deleteTicketComment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(IssueTrackerActions.deleteTicketComment),
      switchMap((action) => {
        return this.issueTrackerService
          .deleteTicketComment(action.commentId)
          .pipe(
            map(() =>
              IssueTrackerActions.deleteTicketCommentSuccess({
                commentId: action.commentId,
              })
            ),
            catchError((error) =>
              of(
                IssueTrackerActions.deleteTicketCommentFailure({
                  error: error.message || "Failed to delete comment",
                })
              )
            )
          );
      })
    );
  });

  downloadAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssueTrackerActions.downloadAttachment),
      switchMap(({ attachmentId, fileName }) =>
        this.issueTrackerService.downloadAttachment(attachmentId).pipe(
          map(({ blob, fileName }) =>
            IssueTrackerActions.downloadAttachmentSuccess({ blob, fileName })
          ),
          catchError((error) =>
            of(
              IssueTrackerActions.downloadAttachmentFailure({
                error: error?.message || "Failed to download attachment",
              })
            )
          )
        )
      )
    )
  );

  previewAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssueTrackerActions.previewAttachment),
      switchMap(({ attachmentId, fileName }) =>
        this.issueTrackerService.previewAttachment(attachmentId).pipe(
          map(({ blob, fileName }) =>
            IssueTrackerActions.previewAttachmentSuccess({ blob, fileName })
          ),
          catchError((error) =>
            of(
              IssueTrackerActions.previewAttachmentFailure({
                error: error?.message || "Failed to preview attachment",
              })
            )
          )
        )
      )
    )
  );

  downloadAttachmentSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(IssueTrackerActions.downloadAttachmentSuccess),
        tap(({ blob, fileName }) => downloadBlob(blob, fileName))
      ),
    { dispatch: false }
  );

  previewAttachmentSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(IssueTrackerActions.previewAttachmentSuccess),
        tap(({ blob }) => openBlobInNewTab(blob))
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private issueTrackerService: IssueTrackerService,
    private store: Store
  ) {}
}
